import axios from 'axios';
import { circleApi, TribeMembershipDetails } from 'src/api/circles';
import { InformationDto } from 'src/api/information';
import { pickCustomerCardAdminFields } from 'src/components/oth/CustomerCardDetails/mapCustomerCard';
import { ContactListMember, ContactShareConsent } from '../types';

const isFallbackStatus = (error: unknown) =>
  axios.isAxiosError(error) && [403, 404].includes(error.response?.status ?? 0);

const DECLINED_CONSENT_VALUES = new Set(['declined', 'no', 'nei', 'false']);

export const parseContactShareConsent = (
  value: unknown
): ContactShareConsent => {
  if (value === false) {
    return 'declined';
  }
  if (value === true || value == null || value === '') {
    return 'approved';
  }

  const normalized = String(value).trim().toLowerCase();
  if (DECLINED_CONSENT_VALUES.has(normalized)) {
    return 'declined';
  }

  return 'approved';
};

export const hasDeclinedContactShare = (member: ContactListMember): boolean =>
  member.contactShareConsent === 'declined';

export const getDeclinedContactIds = (
  contacts: ContactListMember[]
): string[] =>
  contacts.filter(hasDeclinedContactShare).map((member) => member.id);

export const mergeDeclinedExclusions = (
  excludedIds: string[],
  contacts: ContactListMember[]
): string[] => {
  const next = new Set(excludedIds);
  getDeclinedContactIds(contacts).forEach((id) => next.add(id));
  return Array.from(next);
};

export const applyDeclinedExclusionsToSections = <
  T extends { contentTypes: string[]; excludedContactIds: string[] },
>(
  sections: T[],
  contacts: ContactListMember[]
): T[] => {
  const declinedIds = getDeclinedContactIds(contacts);
  if (declinedIds.length === 0) {
    return sections;
  }

  let changed = false;
  const next = sections.map((section) => {
    if (!section.contentTypes.includes('contactList')) {
      return section;
    }

    const merged = mergeDeclinedExclusions(
      section.excludedContactIds,
      contacts
    );
    if (
      merged.length === section.excludedContactIds.length &&
      merged.every((id) => section.excludedContactIds.includes(id))
    ) {
      return section;
    }

    changed = true;
    return { ...section, excludedContactIds: merged };
  });

  return changed ? next : sections;
};

export const toStoredContactMember = (
  member: ContactListMember
): ContactListMember => {
  if (!hasDeclinedContactShare(member)) {
    return member;
  }

  return {
    id: member.id,
    name: member.name,
    avatarUrl: member.avatarUrl,
    contactShareConsent: 'declined',
  };
};

export const mapUnknownMembersToContacts = (
  members: unknown
): ContactListMember[] => {
  if (!Array.isArray(members)) {
    return [];
  }

  return members.flatMap((item) => {
    const record = item as Record<string, any>;
    const user = record.user ?? record;
    const id = user?._id ?? record.user_id;
    if (!id) {
      return [];
    }

    const contactShareConsent = parseContactShareConsent(
      user.contactShareConsent ?? record.contactShareConsent
    );

    return [
      {
        id: String(id),
        name: user.full_name || user.name || 'Unknown member',
        avatarUrl: user.avatar_url,
        ...(user.bio ? { bio: user.bio } : {}),
        phone: user.phone_number ?? user.phone,
        email: user.email,
        company: user.firm ?? user.company ?? undefined,
        notes: user.notes,
        contactShareConsent,
        ...pickCustomerCardAdminFields(user, record),
      },
    ];
  });
};

export const mapTribeMembersToContacts = (
  members: TribeMembershipDetails[]
): ContactListMember[] => mapUnknownMembersToContacts(members);

export const mergeLeaderDirectoryPii = (
  contacts: ContactListMember[],
  leadersDirectory: ContactListMember[]
): ContactListMember[] => {
  const byId = new Map(leadersDirectory.map((member) => [member.id, member]));

  const merged = contacts.map((contact) => {
    const leaderRecord = byId.get(contact.id);
    if (!leaderRecord) {
      return contact;
    }

    return {
      ...contact,
      phone: contact.phone || leaderRecord.phone,
      email: contact.email || leaderRecord.email,
      company: contact.company || leaderRecord.company,
      notes: contact.notes || leaderRecord.notes,
      contactShareConsent:
        contact.contactShareConsent ?? leaderRecord.contactShareConsent,
      ...pickCustomerCardAdminFields(contact, leaderRecord),
    };
  });

  const seenIds = new Set(merged.map((member) => member.id));
  leadersDirectory.forEach((leader) => {
    if (!seenIds.has(leader.id)) {
      merged.push(leader);
      seenIds.add(leader.id);
    }
  });

  return merged;
};

export const extractContactSnapshot = (
  form: InformationDto | null | undefined
): ContactListMember[] => {
  const byId = new Map<string, ContactListMember>();

  (form?.sections ?? []).forEach((section) => {
    (section.contactMembers ?? []).forEach((member) => {
      if (member?.id && !byId.has(member.id)) {
        byId.set(member.id, {
          ...member,
          contactShareConsent: parseContactShareConsent(
            member.contactShareConsent
          ),
        });
      }
    });
  });

  return Array.from(byId.values());
};

export const resolveContactListMembers = (
  liveMembers: ContactListMember[],
  snapshotMembers: ContactListMember[]
): ContactListMember[] => {
  if (liveMembers.length === 0) {
    return snapshotMembers;
  }

  const snapshotById = new Map(
    snapshotMembers.map((member) => [member.id, member])
  );

  const resolved = liveMembers.map((member) => {
    const saved = snapshotById.get(member.id);
    if (!saved) {
      return member;
    }

    const declined = hasDeclinedContactShare(member);

    return {
      ...member,
      name: member.name || saved.name,
      avatarUrl: member.avatarUrl || saved.avatarUrl,
      phone: declined ? member.phone : member.phone || saved.phone,
      email: declined ? member.email : member.email || saved.email,
      company: declined ? member.company : member.company || saved.company,
      notes: member.notes || saved.notes,
      contactShareConsent: parseContactShareConsent(
        member.contactShareConsent ?? saved.contactShareConsent
      ),
      ...pickCustomerCardAdminFields(member, saved),
    };
  });

  const liveIds = new Set(liveMembers.map((member) => member.id));
  snapshotMembers.forEach((saved) => {
    if (liveIds.has(saved.id) || !hasDeclinedContactShare(saved)) {
      return;
    }
    resolved.push({
      ...saved,
      phone: undefined,
      email: undefined,
      company: undefined,
      contactShareConsent: 'declined',
    });
  });

  return resolved;
};

export const countHiddenDueToConsent = (
  liveMembers: ContactListMember[],
  snapshotMembers: ContactListMember[] = [],
  excludedIds: string[] = []
): number => {
  const hiddenIds = new Set(getDeclinedContactIds(liveMembers));
  snapshotMembers.forEach((member) => {
    if (hasDeclinedContactShare(member)) {
      hiddenIds.add(member.id);
    }
  });
  const liveIds = new Set(liveMembers.map((member) => member.id));
  excludedIds.forEach((id) => {
    if (!liveIds.has(id)) {
      hiddenIds.add(id);
    }
  });
  return hiddenIds.size;
};

export const loadCircleContactMembers = async (
  circleId: string,
  canUseLeaderDirectory: boolean
): Promise<ContactListMember[]> => {
  try {
    const contacts = mapUnknownMembersToContacts(
      await circleApi.getCircleContacts(circleId)
    );

    if (!canUseLeaderDirectory) {
      return contacts;
    }

    try {
      const leadersDirectory = mapUnknownMembersToContacts(
        await circleApi.getMembersOfCircle(circleId)
      );
      return mergeLeaderDirectoryPii(contacts, leadersDirectory);
    } catch {
      return contacts;
    }
  } catch (error) {
    if (!isFallbackStatus(error)) {
      throw error;
    }
  }

  if (canUseLeaderDirectory) {
    const members = await circleApi.getMembersOfCircle(circleId);
    return mapUnknownMembersToContacts(members);
  }

  const directory = await circleApi.getCircleMemberDirectory(circleId);
  return mapUnknownMembersToContacts(directory);
};
